import React from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  FUEL_COLORS,
  generateAssetColor,
  formatDateTime,
  formatAUD,
  useContainerWidth,
  CHART_MARGINS,
  defaultLegendProps,
  rotatedTickLabelStyle,
  tickLabelStyle,
} from './shared';

/**
 * Stacked bar chart of revenue (AUD) per time bucket, broken down by asset.
 */
const RevenueBarChart = ({ revenue, assets, frequency }) => {
  const { ref, width } = useContainerWidth();

  if (!revenue || revenue.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">No revenue data available for this period</Typography>
      </Box>
    );
  }

  const assetInfo = {};
  (assets || []).forEach((a, i) => {
    assetInfo[a.asset_id] = { ...a, colorIndex: i };
  });

  const buckets = {};
  const assetIds = new Set();
  revenue.forEach(({ date_time, asset_id, revenue_aud }) => {
    const key = String(date_time);
    if (!buckets[key]) buckets[key] = {};
    buckets[key][asset_id] = revenue_aud;
    assetIds.add(asset_id);
  });

  const sortedBuckets = Object.keys(buckets).sort();
  const sortedAssets = Array.from(assetIds).sort();
  const xLabels = sortedBuckets.map((b) => formatDateTime(b, frequency));

  const assetColor = (assetId, idx) => {
    const info = assetInfo[assetId];
    if (info && info.fuel_code && FUEL_COLORS[info.fuel_code]) return FUEL_COLORS[info.fuel_code];
    return generateAssetColor(idx);
  };

  const series = sortedAssets.map((aid, idx) => ({
    label: assetInfo[aid]?.name || aid,
    data: sortedBuckets.map((b) => buckets[b][aid] || 0),
    stack: 'rev',
    color: assetColor(aid, idx),
  }));

  return (
    <Box ref={ref} sx={{ width: '100%', height: 420, minWidth: 0, overflow: 'hidden' }}>
      {width > 0 && (
        <BarChart
          width={width}
          height={420}
          series={series}
          xAxis={[{
            scaleType: 'band',
            data: xLabels,
            tickLabelStyle: rotatedTickLabelStyle,
            tickNumber: xLabels.length > 30 ? 30 : undefined,
          }]}
          yAxis={[{ valueFormatter: formatAUD, tickLabelStyle }]}
          margin={CHART_MARGINS}
          slotProps={{ legend: defaultLegendProps }}
        />
      )}
    </Box>
  );
};

export default RevenueBarChart;
